/* eslint-disable max-len */
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getSessions as getSessionsApi,
  createSession as createSessionApi,
  getMySessions as getMySessionsApi,
  attendanceSession as attendanceSessionApi
} from 'services/api/home/sessions';
import {
  getOrganizationSession as getOrganizationSessionApi,
  createOrganizationGroupSession as createOrganizationGroupSessionApi,
  updateOrganizationGroupSession as UpdateOrganizationGroupSessionApi,
  getSessionSummary as getSessionSummaryApi,
  changeSessionRecordingStatus as changeSessionRecordingStatusApi,
  deleteOrganizationGroupSession as deleteOrganizationGroupSessionApi
} from 'services/api/organization/session';
import { convertArrayToMap } from 'utils/array';
import { minute } from 'constants/index';
import { format, isToday } from 'date-fns';
import { useDateInterval } from 'utils/hooks/useDateInterval';
import { useAlert, ALERT_MESSAGE } from 'utils/hooks/useAlert';

const GET_SESSIONS = 'GET_SESSIONS';
const CLEAR_SESSIONS = 'CLEAR_SESSIONS';
const GET_ORG_SESSIONS = 'GET_ORG_SESSIONS';
const CREATE_SESSION = 'CREATE_SESSION';
const CREATE_ORG_SESSION = 'CREATE_ORG_SESSION';
const EDIT_ORG_SESSION = 'EDIT_ORG_SESSION';
const GET_SESSION_SUMMARY = 'GET_SESSION_SUMMARY';
const GET_MY_SESSIONS = 'GET_MY_SESSIONS';

const initState = {
  sessions: {
    data: [],
    dataMaps: null,
    total: 0
  },
  mySessions: {
    data: [],
    dataMaps: null,
    total: 0
  },
  sessionSummary: null,
};

const actions = {
  getSessions: sessions => ({
    type: GET_SESSIONS,
    payload: { sessions },
  }),
  clearSessions: () => ({
    type: CLEAR_SESSIONS,
  }),
  getOrganizationSessions: sessions => ({
    type: GET_ORG_SESSIONS,
    payload: { sessions },
  }),
  createSession: params => ({
    type: CREATE_SESSION,
    payload: params
  }),
  createOrganizationSession: params => ({
    type: CREATE_ORG_SESSION,
    payload: params
  }),
  updateOrganizationSession: params => ({
    type: EDIT_ORG_SESSION,
    payload: params
  }),
  getSessionSummary: data => ({
    type: GET_SESSION_SUMMARY,
    payload: data
  }),
  getMySessions: data => ({
    type: GET_MY_SESSIONS,
    payload: data,
  })
};

export const useSessions = () => {
  const { setAlert } = useAlert();
  const { organizationId, classId, sessionId, timeSpanId } = useParams();
  const dispatch = useDispatch();
  const {
    sessions,
    sessionSummary,
    mySessions } = useSelector(state => state.sessions);
  const [{ nowTimeStamp }] = useDateInterval();
  const isCanEnterClass = (startAt, endAt) => {
    let enterClass = {
      canEnter: false,
      type: '',
    };
    if (nowTimeStamp >= endAt) {
      enterClass.type = 'end';
      /**
       * 教師端按鍵文字為查看課程
         學生端按鍵文字為複習課程
       */
      return enterClass;
    }
    if (nowTimeStamp >= startAt - 600000) {
      /**
       * 上課時間前10分開始
          教師端按鍵文字為進入教室
          學生端按鍵文字為進入教室
       */
      enterClass = {
        canEnter: true,
        type: 'before10',
      };
    } else if (nowTimeStamp >= startAt - 1200000) {
      /**
       * 上課時間前20分
          教師端按鍵文字為進入教室
          學生端按鍵文字為預習課程
       */
      enterClass = {
        canEnter: true,
        type: 'before20'
      };
    }else if (nowTimeStamp >= startAt - 3600000) {
      /**
       * 上課時間前60分
          教師端按鍵文字為進入教室
          學生端按鍵文字為預習課程
          教師端可簽到時間
       */
      enterClass = {
        canEnter: true,
        type: 'before60'
      };
    } else if (nowTimeStamp < startAt) {
      /**
       * 上課時間未到
          按鍵文字為預習課程
       */
      enterClass = {
        canEnter: true,
        type: 'noStart',
      };
    }
    return enterClass;
  };

  const getSessions = async params => {
    const { data, isSuccess } = await getSessionsApi(classId)(params);
    if (!isSuccess) return;
    const { sessions, total } = data;
    const newSessions = sessions.map(item => ({
      ...item,
      subject:item.subjects.length > 0 && item.subjects[0].name,
      startDate: format(new Date(item.startAt), 'yyyy-MM-dd'),
      startTime: `${format(new Date(item.startAt), 'HH:mm')}
       ~ ${format(new Date(item.endAt), 'HH:mm')} ( ${((item.endAt - item.startAt) / 1000 / 60)}分 )`,
      duration: ((item.endAt - item.startAt) / 1000 / 60) + '分鐘',
      isCanEnterClass: isCanEnterClass(item.startAt, item.endAt),
      exam:'',
    }));
    dispatch(actions.getSessions({ data: newSessions, total }));
  };

  const getOrganizationSessions = async (params, signal) => {
    const response = await getOrganizationSessionApi(organizationId, classId)(params)(signal);
    if(!response) return;
    const { data, isSuccess } = response;
    if (!isSuccess) return;
    const { sessions, total } = data;
    const sessionStatusText = {
      notStart: '尚未開始',
      isFinished: '已結束',
      inProgress:'進行中'
    };
    const sessionStatus = (startAt, endAt) => {
      const nowTime = new Date().getTime();
      if(nowTime < startAt) return 'notStart';
      if(nowTime > endAt) return 'isFinished';
      if(nowTime >= startAt && endAt >= nowTime) return 'inProgress';
    };

    const newSessions = sessions.map(item => ({
      ...item,
      subject:item.subjects.length > 0 && item.subjects[0].name,
      startDate: format(new Date(item.startAt), 'yyyy-MM-dd'),
      startTime: format(new Date(item.startAt), 'HH:mm'),
      duration: ((item.endAt - item.startAt) / 1000 / 60) + '分鐘',
      isCanEnterClass: isCanEnterClass(item.startAt, item.endAt),
      sessionStatus: sessionStatusText[sessionStatus(item.startAt,item.endAt)],
      resourcesText: item.resources.join(','),
      productId: item.product.productId,
      productName: item.product.productName,
    }));
    const sessionsMap = convertArrayToMap(newSessions, 'id');
    dispatch(actions.getOrganizationSessions({ data: newSessions, dataMaps: sessionsMap, total }));
  };

  const createSession = async params => {
    try {
      dispatch(actions.createSession(params));
      const { isSuccess, error } = await createSessionApi(classId)(params);
      if (!isSuccess) throw error;
      setAlert(ALERT_MESSAGE.CREATE_SUCCESS, 'success');
      return isSuccess;
    } catch (error) {
      const { errorCode } = error;
      setAlert(ALERT_MESSAGE.CREATE_FAIL, 'error', errorCode);
    }
  };

  const createOrganizationSession = async params => {
    try {
      dispatch(actions.createOrganizationSession(params));
      const { isSuccess, error, data } = await createOrganizationGroupSessionApi(organizationId, classId)(params);
      if (!isSuccess) throw error;
      setAlert(ALERT_MESSAGE.CREATE_SUCCESS, 'success');
      return { isSuccess, data };
    } catch (error) {
      const { errorCode } = error;

      if(errorCode === 40005) {
        setAlert('教師該時段已有課程', 'error', errorCode);
      } else {
        setAlert(ALERT_MESSAGE.CREATE_FAIL, 'error', errorCode);
      }
    }
  };

  const updateOrganizationSession = async params => {
    try {
      dispatch(actions.updateOrganizationSession(params));
      const { isSuccess, error, data } = await UpdateOrganizationGroupSessionApi(organizationId, classId, sessionId)(params);
      if (!isSuccess) throw error;
      setAlert(ALERT_MESSAGE.UPDATE_SUCCESS, 'success');
      return { isSuccess, data };
    } catch (error) {
      const { errorCode } = error;

      if(errorCode === 40005) {
        setAlert('教師該時段已有課程', 'error', errorCode);
      } else {
        setAlert(ALERT_MESSAGE.CREATE_FAIL, 'error', errorCode);
      }
    }
  };

  const clearSessions = () => dispatch(actions.clearSessions());

  const getSessionSummary = async () => {
    const { isSuccess, data } = await getSessionSummaryApi(organizationId, sessionId, timeSpanId)();
    if (!isSuccess) return;
    dispatch(actions.getSessionSummary(data));
  };

  const getMySessions = async params => {
    const { isSuccess, data: { sessions, total } } = await getMySessionsApi(params);
    if (!isSuccess) return;
    const nowTime = new Date().getTime();
    sessions.forEach(item => {
      item.during = (item.endAt - item.startAt) / minute;
      if (item.endAt < nowTime) {
        item.classType = 'over';
      }
      else if (nowTime > (item.startAt - minute) && item.endAt > nowTime) {
        item.classType = 'class';
      }
      else if (isToday(item.startAt)) {
        item.classType = 'pendding';
      }
      else if (item.startAt > nowTime) {
        item.classType = 'yet';
      } else {
        item.classType = 'over';
      }
    });
    dispatch(actions.getMySessions({ sessions, total }));
  };

  const attendanceSession = async  (id,timeSpanId,payload) => {
    const { isSuccess,data } = await attendanceSessionApi(id,timeSpanId)(payload);
    if(!isSuccess){
      setAlert(ALERT_MESSAGE.ATTENDANCE_FAIL, 'fail');
      return;
    }
    setAlert(ALERT_MESSAGE.ATTENDANCE_SUCCESS, 'success');
    const newSessions = sessions.data.map((item) => {
      if(item.id === data.id){
        return {
          ...item,
          ...data,
          subject:data.subjects.length > 0 && data.subjects[0].name,
          startDate: format(new Date(data.startAt), 'yyyy-MM-dd'),
          startTime: `${format(new Date(item.startAt), 'HH:mm')}
          ~ ${format(new Date(item.endAt), 'HH:mm')} ( ${((item.endAt - item.startAt) / 1000 / 60)}分 )`,
          duration: ((data.endAt - data.startAt) / 1000 / 60) + '分鐘',
          isCanEnterClass: isCanEnterClass(data.startAt, data.endAt)
        };
      }
      return item;
    });
    const sessionsMap = convertArrayToMap(newSessions, 'id');
    dispatch(actions.getOrganizationSessions({ data: newSessions, dataMaps: sessionsMap, total:sessions.total }));
  };

  const changeSessionRecordingStatus = async (sessionId,timeSpanId,payload) => {
    const { isSuccess,data } = await changeSessionRecordingStatusApi(organizationId,sessionId,timeSpanId)(payload);
    if(!isSuccess){
      setAlert('錄影狀態變更失敗', 'fail');
      return isSuccess;
    }
    if(data.recordingStatus === 'stopped'){
      setAlert('課程錄影結束', 'success');
    }else{
      setAlert('課程錄影開始', 'success');
    }
    return isSuccess;
  };

  const deleteOrganizationGroupSession = async (targetClassId,sessionId,timeSpanId) => {
    const { isSuccess } = await deleteOrganizationGroupSessionApi(organizationId,targetClassId,sessionId,timeSpanId)();
    if(!isSuccess){
      setAlert('刪除課程失敗', 'fail');
    }else{
      setAlert('刪除課程成功', 'success');
    }
    return isSuccess;
  };

  return [
    { sessions, sessionSummary, mySessions, }, // state
    {
      getSessions,
      clearSessions,
      createSession,
      getOrganizationSessions,
      createOrganizationSession,
      updateOrganizationSession,
      getSessionSummary,
      getMySessions,
      attendanceSession,
      changeSessionRecordingStatus,
      deleteOrganizationGroupSession,
    }, // eventHanlder
  ];
};

const reducer = (state = initState, action) => {
  switch (action.type) {
    case GET_SESSIONS: {
      const { data, total } = action.payload.sessions;
      return {
        ...state,
        sessions: {
          data,
          total,
          dataMap: convertArrayToMap(data,'id')
        },
      };
    }
    case CLEAR_SESSIONS: {
      return {
        ...state,
        sessions: {
          ...initState.sessions
        }
      };
    }
    case GET_ORG_SESSIONS: {
      return {
        ...state,
        sessions: action.payload.sessions,
      };
    }
    case GET_SESSION_SUMMARY: {
      return {
        ...state,
        sessionSummary: action.payload
      };
    }
    case GET_MY_SESSIONS: {
      return {
        ...state,
        mySessions: {
          data: action.payload.sessions,
          total: action.payload.total
        },
      };
    }
    default:
      return state;
  }
};

export default reducer;
